// @need-translate
import {isNil} from 'lodash';
import {ReactNode} from 'react';
import {SchemeLink} from '../../../applink/SchemeLink';
import {t} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {getTitleWithParsedEpisode} from '../../../domain/content/[seriesId]/util/getTitleWithParsedEpisode';
import {SingleBadgeType} from '../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Image} from '../../atoms/Image/Image';
import {SeparatorType} from '../../atoms/Separator/SeparatorType.enum';
import {StaticImage} from '../../atoms/StaticImage/StaticImage';
import {Text} from '../../atoms/Text/Text';
import {CheckBox} from '../../molecules/CheckBox/CheckBox';
import {LabelBadge} from '../../molecules/LabelBadge/LabelBadge';
import {LabelBadge16Type} from '../../molecules/LabelBadge/LabelBadge16Type.enum';
import {LabelBadgeSizeType} from '../../molecules/LabelBadge/LabelBadgeSizeType.enum';
import {TextsWithSeparator} from '../../molecules/TextsWithSeparator/TextsWithSeparator';
import {ThumbnailRatio} from '../../molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {SingleListViewItemProps} from './props.interface';

/**
 * 회차리스트. 일반적으로 회차를 표현할 때 쓰입니다.
 * 콘텐츠 홈
 */
export function SingleListViewItem({
  item: {
    thumbnail,
    showPlayerIcon,
    isCheckMode,
    isChecked,
    row1,
    row2,
    row3,
    scheme,
    isViewed,
    single,
  },
  isMobile,
  onClickThumbnail,
  isBookmarked,
  parseEpisode = false,
  webAccessible,
  imageLazyLoad,
  onClick,
  className,
  discountRate,
  ...rest
}: SingleListViewItemProps) {
  return (
    <div
      {...rest}
      onClick={e => LOG_CLICK_EVENT('회차_클릭', e, onClick)}
      className={twMerge(
        'relative flex',
        isMobile ? 'bg-theme-bg-surface' : 'bg-theme-bg-surface-alt',
        isViewed && 'rounded-6pxr bg-theme-transparent-20',
        className
      )}
    >
      {isCheckMode && (
        <div className="mr-14pxr flex shrink-0 items-center">
          <CheckBox checked={isChecked} />
        </div>
      )}
      <SchemeLink
        scheme={scheme || undefined}
        preventEvent={!!onClick}
        linkClassName={twMerge(
          'flex flex-1',
          !webAccessible && 'items-center justify-between'
        )}
      >
        <div className="flex items-center">
          <div
            className={twMerge(
              'relative mr-14pxr w-56pxr shrink-0 overflow-hidden',
              isViewed
                ? 'rounded-l-6pxr after:rounded-l-6pxr'
                : 'rounded-6pxr after:rounded-6pxr',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:outline-solid after:outline-[0.5px] after:-outline-offset-[0.5px] after:outline-static-transparent-black-05 after:content-[""]'
            )}
            {...putTiaraObjectProps({
              customProps: {event_episode_id: String(single.productId)},
            })}
            onClick={e => {
              LOG_CLICK_EVENT('회차썸네일_클릭', e, e => {
                e.preventDefault();
                e.stopPropagation();
                onClickThumbnail(thumbnail);
              });
            }}
          >
            <Image
              src={thumbnail}
              dimmed={!!isViewed}
              alt="썸네일"
              className="h-full w-full"
              ratio={ThumbnailRatio[ThumbnailType.Poster2To3]}
              isLazy={imageLazyLoad}
            />
            {showPlayerIcon && (
              <div className="absolute bottom-0 right-0 mb-30pxr mr-15pxr">
                <Icon
                  className="shrink-0"
                  type={IconType.PlayerPlaySolidLStatic}
                  disableActiveEffect
                />
              </div>
            )}
          </div>
          <div className="flex flex-col pr-14pxr">
            {row1 && (
              <div className="mb-1pxr flex items-center">
                {!parseEpisode && (
                  <Text className="font-medium2 line-clamp-1 break-all text-theme-solid-100">
                    {row1 || ''}
                  </Text>
                )}
                {parseEpisode && (
                  <>
                    <Text className="font-medium2 line-clamp-1 break-all text-theme-solid-100">
                      {getTitleWithParsedEpisode(row1).title || ''}
                      {getTitleWithParsedEpisode(row1).episode && <>&nbsp;</>}
                    </Text>
                    <Text className="font-medium2 inline-flex shrink-0 grow text-theme-solid-100">
                      {getTitleWithParsedEpisode(row1).episode || ''}
                    </Text>
                  </>
                )}
              </div>
            )}
            {row2 && (
              <TextsWithSeparator
                texts={row2}
                separatorType={SeparatorType.Dot}
                className="font-x-small1 h-16pxr text-theme-solid-60"
              />
            )}
            <div className="mt-7pxr flex items-center">
              {row3?.badgeList && row3?.badgeList.length > 0 && (
                <div className="mr-6pxr flex items-center space-x-2pxr">
                  {row3.badgeList.map(
                    badge => SINGLE_BADGE_TYPE_LABEL_BADGE_MAP[badge]
                  )}
                </div>
              )}
              {row3?.text && (
                <Text className="font-x-small1 line-clamp-1 shrink-0 text-ellipsis break-all text-theme-solid-60">
                  {row3.text}
                </Text>
              )}
              {row3?.priceList && row3.priceList.length > 0 && (
                <>
                  {row3?.text && (
                    <StaticImage imageType={IconType.ListMetadataDot} />
                  )}
                  <TextsWithSeparator
                    texts={row3?.priceList}
                    separatorType={SeparatorType.Dot}
                    className={twMerge(
                      'font-x-small1 text-theme-solid-100',
                      !isNil(discountRate) &&
                        discountRate > 0 &&
                        'text-static-point-red'
                    )}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        {!webAccessible && (
          <Text className="font-x-small1 mr-5pxr break-all text-center text-theme-solid-60">
            웹에서
            <br />
            감상불가
          </Text>
        )}
      </SchemeLink>
      {isBookmarked && (
        <StaticImage
          className="absolute right-0 top-0"
          imageType={
            isMobile ? IconType.ContentHomeMark : IconType.ContentHomeMarkPc
          }
        />
      )}
    </div>
  );
}

const SINGLE_BADGE_TYPE_LABEL_BADGE_MAP: {
  [key in SingleBadgeType]: ReactNode;
} = {
  [SingleBadgeType.BadgeUpStatic]: (
    <LabelBadge
      key={SingleBadgeType.BadgeUpStatic}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.badge}
      badge={IconType.BadgeUpStatic}
    />
  ),
  [SingleBadgeType.Badge15Static]: (
    <LabelBadge
      key={SingleBadgeType.Badge15Static}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.badge}
      badge={IconType.Badge15Static}
    />
  ),
  [SingleBadgeType.Badge19Static]: (
    <LabelBadge
      key={SingleBadgeType.Badge19Static}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.badge}
      badge={IconType.Badge19Static}
    />
  ),
  [SingleBadgeType.Badge19SelfStatic]: (
    <LabelBadge
      key={SingleBadgeType.Badge19SelfStatic}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.badge}
      badge={IconType.Badge19SelfStatic}
    />
  ),
  [SingleBadgeType.BadgeTimeStatic]: (
    <LabelBadge
      key={SingleBadgeType.BadgeTimeStatic}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.badge}
      badge={IconType.BadgeTimeStatic}
    />
  ),
  [SingleBadgeType.LabelBadgeFree]: (
    <LabelBadge
      key={SingleBadgeType.LabelBadgeFree}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.line}
      label={t('common_free')}
    />
  ),
  [SingleBadgeType.LabelBadgeRent]: (
    <LabelBadge
      key={SingleBadgeType.LabelBadgeRent}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.solid}
      label={t('common_content_rental')}
    />
  ),
  [SingleBadgeType.LabelBadgeOwn]: (
    <LabelBadge
      key={SingleBadgeType.LabelBadgeOwn}
      sizeType={LabelBadgeSizeType.Size16}
      type={LabelBadge16Type.solid}
      label={t('common_content_possession')}
    />
  ),
};
